import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, Mail, MapPin, Phone } from "lucide-react";
import { CATEGORIES, BRAND, buildWhatsAppLink } from "@/data/lakaData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  treatment: "",
  preferred_day: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Completa nombre, teléfono y email para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Solicitud enviada. Te contactaremos muy pronto.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "No hemos podido enviar tu solicitud. Prueba por WhatsApp.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const wa = buildWhatsAppLink(
    "Hola LAKA, me gustaría reservar una valoración personalizada.",
  );

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32"
      style={{ background: "#1A1A1A", color: "#FBF6F2" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="laka-eyebrow">Reserva tu cita</span>
          <h2 className="mt-4 font-serif-laka text-4xl md:text-5xl lg:text-[3.2rem] font-light leading-[1.05] text-[#FBF6F2]">
            Reserva tu <span className="italic text-[#D6B36A]">valoración personalizada</span>
          </h2>
          <p className="mt-6 text-[#FBF6F2]/70 leading-relaxed font-light">
            Cuéntanos qué te gustaría cuidar. Te contactamos por teléfono o
            WhatsApp para orientarte con el mejor plan.
          </p>

          <div className="mt-10 space-y-6">
            <a
              href={BRAND.phoneHref}
              data-testid="contact-phone-link"
              className="flex items-start gap-4 group"
            >
              <Phone
                className="w-5 h-5 text-[#D6B36A] mt-1 shrink-0"
                strokeWidth={1.4}
              />
              <div>
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#FBF6F2]/50">
                  Teléfono / WhatsApp
                </div>
                <div className="text-lg text-[#FBF6F2] group-hover:text-[#D6B36A] transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-link"
              className="flex items-start gap-4 group"
            >
              <Mail
                className="w-5 h-5 text-[#D6B36A] mt-1 shrink-0"
                strokeWidth={1.4}
              />
              <div>
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#FBF6F2]/50">
                  Email
                </div>
                <div className="text-lg text-[#FBF6F2] group-hover:text-[#D6B36A] transition-colors">
                  {BRAND.email}
                </div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin
                className="w-5 h-5 text-[#D6B36A] mt-1 shrink-0"
                strokeWidth={1.4}
              />
              <div>
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#FBF6F2]/50">
                  Centro
                </div>
                <div className="text-sm text-[#FBF6F2]/90 leading-relaxed">
                  {BRAND.address}
                </div>
              </div>
            </div>
          </div>

          <a
            data-testid="contact-whatsapp-btn"
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="mt-10 laka-btn-primary laka-btn-gold inline-flex"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
            Hablar por WhatsApp
          </a>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-[#FBF6F2] text-[#1A1A1A] p-8 md:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nombre" required>
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={set("name")}
                  type="text"
                  className="laka-input"
                  placeholder="Tu nombre"
                  required
                />
              </Field>
              <Field label="Teléfono" required>
                <input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  type="tel"
                  className="laka-input"
                  placeholder="+34"
                  required
                />
              </Field>
              <Field label="Email" required className="sm:col-span-2">
                <input
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={set("email")}
                  type="email"
                  className="laka-input"
                  placeholder="tu@email.com"
                  required
                />
              </Field>
              <Field label="Tratamiento de interés">
                <select
                  data-testid="contact-input-treatment"
                  value={form.treatment}
                  onChange={set("treatment")}
                  className="laka-input"
                >
                  <option value="">Selecciona una opción</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.label}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Día preferido">
                <input
                  data-testid="contact-input-day"
                  value={form.preferred_day}
                  onChange={set("preferred_day")}
                  type="text"
                  className="laka-input"
                  placeholder="Ej. martes por la tarde"
                />
              </Field>
              <Field label="Mensaje" className="sm:col-span-2">
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  className="laka-input resize-none"
                  placeholder="Cuéntanos qué necesitas..."
                />
              </Field>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="laka-btn-primary disabled:opacity-60"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} />
                {submitting ? "Enviando..." : "Enviar solicitud"}
              </button>
              <a
                data-testid="contact-whatsapp-form-btn"
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="laka-btn-ghost"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                WhatsApp
              </a>
            </div>
            <p className="mt-5 text-[0.7rem] text-[#1A1A1A]/50 leading-relaxed">
              Al enviar este formulario aceptas ser contactada por LAKA CLUB
              BEAUTY para gestionar tu solicitud de cita.
            </p>
          </form>
        </div>
      </div>

      <style>{`
        .laka-input {
          width: 100%;
          padding: 12px 14px;
          background: #FBF6F2;
          border: 1px solid rgba(26,26,26,0.15);
          border-radius: 2px;
          font-family: 'Montserrat', sans-serif;
          font-size: 0.9rem;
          color: #1A1A1A;
          transition: border-color 250ms ease, box-shadow 250ms ease;
          outline: none;
        }
        .laka-input:focus {
          border-color: #D6B36A;
          box-shadow: 0 0 0 3px rgba(214,179,106,0.15);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-[#1A1A1A]/60 font-medium">
      {label} {required && <span className="text-[#D6B36A]">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default Contact;
